import { once, removeListener, run } from '../../helper/electron';
import { DuplicateGroupItem, Track } from '../../types/Track';

type RequestOptions = {
  /** Reply arrives on a different channel than the request was sent on. */
  replyChannel?: string;
  timeoutMs?: number;
};

/**
 * Send on a channel and resolve with the first reply. Rejects on timeout, and
 * detaches its listener either way so a dropped reply cannot leak.
 */
export const request = <T = unknown>(
  channel: string,
  payload?: unknown,
  { replyChannel, timeoutMs = 10000 }: RequestOptions = {},
): Promise<T> => {
  const listenChannel = replyChannel ?? channel;

  return new Promise<T>((resolve, reject) => {
    const listener = (response: T) => {
      clearTimeout(timeoutId);
      resolve(response);
    };

    const timeoutId = setTimeout(() => {
      removeListener(listenChannel, listener);
      reject(new Error(`Timeout: no response on ${listenChannel}`));
    }, timeoutMs);

    once(listenChannel, listener);
    run(channel, payload);
  });
};

/** Resolves with the reply, or with `fallback` if the request fails. */
export const requestOrDefault = async <T>(
  fallback: T,
  channel: string,
  payload?: unknown,
  options?: RequestOptions,
): Promise<T> => {
  try {
    return (await request<T>(channel, payload, options)) ?? fallback;
  } catch (error) {
    console.warn(`${channel} failed:`, error);
    return fallback;
  }
};

/** Backend replies that mean "this succeeded" carry no consistent shape. */
const readDeleteOutcome = (response: unknown): { error?: string } => {
  if (
    response === undefined ||
    response === true ||
    response === 'ok' ||
    response === 'success' ||
    (typeof response === 'number' && response > 0)
  ) {
    return {};
  }
  if (typeof response === 'number' && response === 0) {
    return { error: 'No rows affected (possible file lock or database issue)' };
  }
  if (response && typeof response === 'object') {
    const payload = response as { success?: boolean; error?: string };
    if (payload.success === true) return {};
    return { error: payload.error || 'Failed response' };
  }
  return { error: 'Unknown error' };
};

/**
 * Move one track to the recycle bin. Throws with a displayable message so
 * callers can collect failures per track.
 */
export const deleteTrack = async (
  trackId: number,
  timeoutMs = 10000,
): Promise<void> => {
  const response = await request<unknown>(
    'my-music/delete-track',
    { track: { id: trackId }, options: { purge: true } },
    { timeoutMs },
  );
  const { error } = readDeleteOutcome(response);
  if (error) throw new Error(error);
};

const requireSuccess = async (
  channel: string,
  payload: unknown,
  failureMessage: string,
): Promise<void> => {
  const response = await request<{ success?: boolean; error?: string }>(
    channel,
    payload,
  );
  if (response?.success !== true) {
    throw new Error(response?.error || failureMessage);
  }
};

export const saveIgnoredGroup = (group: DuplicateGroupItem): Promise<void> =>
  requireSuccess(
    'duplicates/save-ignored-group',
    {
      groupName: group.name,
      groupData: {
        id: group.id,
        name: group.name,
        trackCount: group.trackCount ?? group.tracks.length,
        ignoredAt: new Date().toISOString(),
      },
    },
    'Failed to save ignored group',
  );

export const unignoreGroup = (group: DuplicateGroupItem): Promise<void> =>
  requireSuccess(
    'duplicate-resolver/unignore-group',
    { groupName: group.name, groupId: group.id },
    'Failed to unignore group',
  );

export const loadIgnoredGroupNames = async (): Promise<string[]> => {
  const response = await requestOrDefault<{ ignoredGroups: string[] }>(
    { ignoredGroups: [] },
    'duplicates/load-ignored-groups',
    undefined,
    { timeoutMs: 30000 },
  );
  return Array.isArray(response.ignoredGroups) ? response.ignoredGroups : [];
};

export type PersistedGroupState = { isResolved: boolean; isIgnored: boolean };

export const loadSession = (sessionId: string) =>
  requestOrDefault<{
    success: boolean;
    groups: Record<string, PersistedGroupState>;
  }>(
    { success: false, groups: {} },
    'duplicates/load-session',
    { sessionId },
    { timeoutMs: 30000 },
  );

export const saveGroupState = (
  sessionId: string,
  groupId: string,
  isResolved: boolean,
  isIgnored: boolean,
): void => {
  const now = new Date().toISOString();
  run('duplicates/save-group-state', {
    sessionId,
    groupId,
    state: {
      isResolved,
      isIgnored,
      resolvedAt: isResolved ? now : undefined,
      ignoredAt: isIgnored ? now : undefined,
    },
  });
  once('duplicates/save-group-state', (res: { success?: boolean }) => {
    if (!res?.success) console.warn('saveGroupState failed');
  });
};

export const fetchDuplicateTracks = () =>
  request<{ tracks?: Track[] }>(
    'my-music/get-paginated',
    {
      page: 0,
      limit: 10000,
      filters: { filterType: 'duplicates', includeIgnored: true },
      sortField: 'id',
      sortOrder: 'ASC',
    },
    // A full-library duplicate scan can take minutes on large collections.
    { timeoutMs: 300000 },
  );

/** Serato replies on a per-track channel so concurrent lookups cannot cross. */
export const fetchSeratoCratesForTrack = (track: Track): Promise<string[]> =>
  request<string[]>(
    'serato/get-track-crates',
    {
      filePath: track.filePath,
      replyChannel: `serato/get-track-crates:${track.id}`,
    },
    { replyChannel: `serato/get-track-crates:${track.id}` },
  );

export type CrateContentsEntry = {
  file: string;
  name: string;
  tracks: string[];
};

/** Every crate and its track paths in one pass — no per-track scanning. */
export const fetchCrateIndex = () =>
  requestOrDefault<CrateContentsEntry[]>(
    [],
    'serato/get-crate-index',
    undefined,
    { timeoutMs: 180000 },
  );

export type ReconcileCratesResult = {
  dryRun?: boolean;
  crates?: Array<{
    crateFile: string;
    removed: number;
    repointed: number;
    merged: number;
    changed?: boolean;
    backupPath?: string;
  }>;
  removed?: number;
  repointed?: number;
  merged?: number;
  backupDir?: string;
  failures?: string[];
};

/** Repoint or remove crate entries after duplicate files move to the recycle bin. */
export const reconcileSeratoCrates = (payload: {
  remove: string[];
  repoint: Record<string, string>;
  dryRun?: boolean;
}) =>
  requestOrDefault<ReconcileCratesResult>(
    { failures: [] },
    'serato/reconcile-crates',
    payload,
    { timeoutMs: 180000 },
  );

/** Header badge listens on the window for its new count. */
export const refreshRecycleBinBadge = (): void => {
  run('recycle-bin/get-count');
  once(
    'recycle-bin/get-count',
    (response: { success: boolean; count?: number }) => {
      if (response?.success && typeof response.count === 'number') {
        window.dispatchEvent(
          new CustomEvent('recycle-bin/badge-updated', {
            detail: { count: response.count },
          }),
        );
      }
    },
  );
};
